import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText, Stack,
} from '../../../ui';
import StateLink from '../../../StateLink';
import { StyleSeatTextLogo } from '../../../ui/Icons';
import { PROVIDER_NAVIGATION } from './constants';
import { RootState } from '../../../../store/models';
import { useResponsive } from '../../../../hooks/useResponsiveEffect';
import AccountTools from '../AccountTools';
import { Z_FG, Z_NAVIGATION } from '../../../ui/utilities/zIndex';

/**
 * This component is responsible for Provider navigation on desktop
 * This includes:
 *  - StyleSeat Logo
 *  - Main navigation items
 *  - Admin menu (for superusers)
 *  - Notification Feed (icon & dropdown)
 *  - Pro account dropdown menu
 *  - Secondary navigation items when applicable
 */
const TopBarNavigation = ({
  providerId,
}) => {
  const currentRouteName = useSelector<RootState, string>(state => state.route.name);
  const isBlockedFromApplication = useSelector<RootState, boolean>(
    state => state.subsBlocker?.isBlockedFromApplication,
  );
  const responsive = useResponsive();

  const activeTab = PROVIDER_NAVIGATION.find(nav => (
    nav.link === currentRouteName || nav.subNav?.find(subNav => (
      subNav.link === currentRouteName
    ))
  ));
  const {
    subNav: subNavItems = [],
    title: activeTabTitle,
  } = activeTab || {};

  const activeSubTab = subNavItems?.find(subNav => (
    subNav.link === currentRouteName
  ));

  const hasSubNav = subNavItems.length > 0;

  return (
    <React.Fragment>
      <Stack
        // Note that react-native won't support fixed position
        // But this currently works with react-native-web
        // @ts-expect-error
        position="fixed"
        zIndex={Z_NAVIGATION}
        color={colors.whiteWash}
        minWidth="100%"
      >
        {/* Main Nav Toolbar */}
        <Inline
          borderBottomWidth={1}
          borderStyle="solid"
          borderColor={colors.grey3}
          alignItems="center"
          justifyContent="space-between"
          paddingX={20}
          zIndex={Z_FG}
        >
          <Inline alignItems="center">
            {/* StyleSeat logo home page link */}
            <Box paddingRight={16}>
              <StateLink
                to="home"
                params={{ providerId }}
                testID="header-logo"
                style={{ cursor: 'pointer' }}
              >
                <StyleSeatTextLogo />
              </StateLink>
            </Box>
            {PROVIDER_NAVIGATION.map(nav => {
              const isActive = nav.title === activeTabTitle;

              if (nav?.shouldHide?.(responsive)) {
                return null;
              }
              return (
                <Box
                  key={nav.title}
                  flexGrow={1}
                >
                  <StateLink
                    to={nav.link}
                    params={{ providerId }}
                    style={{ cursor: 'pointer' }}
                    clickTracking={{
                      name: 'pro_desktop_topbar_click',
                      params: {
                        tab: nav.title?.toLowerCase?.(),
                      },
                    }}
                  >
                    <Box
                      minHeight={60}
                      justifyContent="center"
                      alignItems="center"
                      paddingX={16}
                      testID={nav.title}
                      data-active={isActive}
                    >
                      <BodyText
                        color={isActive ? colors.metal : colors.ash}
                        weight={isActive ? 'extra-bold' : undefined}
                      >
                        {nav.title}
                      </BodyText>
                    </Box>
                  </StateLink>
                </Box>
              );
            })}
          </Inline>
          <AccountTools
            providerId={providerId}
            isBlockedFromApplication={isBlockedFromApplication}
          />
        </Inline>
        {/* Sub Navigation menu, when applicable */}
        {!hasSubNav ? null : (
          <Inline
            borderBottomWidth={1}
            borderStyle="solid"
            borderColor={colors.grey3}
            alignItems="center"
            paddingX={20}
            testID="proSubNavigation"
          >
            {subNavItems.map((subNavItem, index) => (
              <Box
                key={subNavItem.title}
                paddingX={12}
                paddingLeft={index > 0 ? 12 : 0}
              >
                <StateLink
                  to={subNavItem.link}
                  params={{ providerId }}
                  style={{ cursor: 'pointer' }}
                >
                  <Box
                    minHeight={36}
                    justifyContent="center"
                    alignItems="center"
                    testID={subNavItem.title}
                    data-active={activeSubTab.title === subNavItem.title}
                  >
                    <BodyText
                      color={activeSubTab.title === subNavItem.title ? colors.metal : colors.ash}
                      weight={activeSubTab.title === subNavItem.title ? 'extra-bold' : undefined}
                      appearance="small"
                      textTransform="uppercase"
                    >
                      {subNavItem.title}
                    </BodyText>
                  </Box>
                </StateLink>
              </Box>
            ))}
          </Inline>
        )}
      </Stack>
      {/* Spacer to push down page content */}
      <Box
        minWidth="100%"
        maxHeight={hasSubNav ? 61 + 37 : 61}
        minHeight={hasSubNav ? 61 + 37 : 61}
        testID="proNavigationSpacer"
      />
    </React.Fragment>
  );
};

export default TopBarNavigation;
